import React, { useState, useContext, useEffect, useCallback } from 'react';
import { FiMail, FiLock, FiEye, FiEyeOff, FiLogIn, FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { ShopContext } from '../../context/ShopContext';
import { Helmet } from 'react-helmet';
import axios from 'axios';

const AuthStatusModal = ({ status, onClose }) => {
  const statusConfig = {
    loading: {
      icon: <FiLoader className="animate-spin text-gray-800 text-4xl" />,
      title: "Authenticating...",
      message: "Please wait while we verify your credentials",
      color: "text-gray-800"
    },
    success: {
      icon: <FiCheckCircle className="text-green-500 text-4xl" />,
      title: "Login Successful!",
      message: "You're being redirected to your account",
      color: "text-green-500"
    },
    error: {
      icon: <FiXCircle className="text-red-500 text-4xl" />,
      title: "Authentication Failed",
      message: "Please check your credentials and try again",
      color: "text-red-500"
    }
  };

  const currentStatus = statusConfig[status] || statusConfig.loading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full text-center">
        <div className="mb-4">{currentStatus.icon}</div>
        <h3 className={`text-xl font-medium mb-2 ${currentStatus.color}`}>
          {currentStatus.title}
        </h3>
        <p className="text-gray-600 mb-4">{currentStatus.message}</p>
        {status === 'error' && (
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};

const Login = () => {
  const { navigate, backendUrl, token, setToken } = useContext(ShopContext);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [authStatus, setAuthStatus] = useState(null);

  useEffect(() => {
    if (token) {
      navigate('/');
    }
  }, [token, navigate]);

  const handleChange = useCallback((e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }, [formData]);

  const validateForm = useCallback(() => {
    let isValid = true;
    
    if (!formData.email || !formData.password) {
      setAuthStatus('error');
      isValid = false;
    }
    
    if (formData.password.length < 6) {
      setAuthStatus('error');
      isValid = false;
    }
    
    if (!agreeTerms) {
      setAuthStatus('error');
      isValid = false;
    }
    
    return isValid;
  }, [formData, agreeTerms]);

  const handleAuth = useCallback(async (authFn, showLoading = true) => {
    if (showLoading) setAuthStatus('loading');
    try {
      await authFn();
      setAuthStatus('success');
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      setAuthStatus('error');
    }
  }, [navigate]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    await handleAuth(async () => {
      const response = await axios.post(
        `${backendUrl}/api/user/login`,
        formData,
        { headers: { 'Content-Type': 'application/json' } }
      );

      localStorage.setItem('token', response.data.token);
      setToken(response.data.token);
      localStorage.setItem('user', JSON.stringify({ name: response.data.name, email: response.data.email }));
    });
  }, [formData, validateForm, backendUrl, setToken, handleAuth]);

  const handleGoogleSuccess = useCallback((credentialResponse) => {
    handleAuth(async () => {
      const response = await axios.post(
        `${backendUrl}/api/user/auth/google`,
        { credential: credentialResponse.credential }
      );

      localStorage.setItem('token', response.data.token);
      setToken(response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('googleAuth', JSON.stringify(true));
    }, false); // Don't show loading for Google auth
  }, [backendUrl, setToken, handleAuth]);

  return (
    <>
      <Helmet>
        <title>Login | Sahla Curtain Center - Secure Customer Sign In</title>
        <meta name="description" content="Sign in to your Sahla Curtain Center account for a personalized shopping experience. Secure login for premium curtains, orders, and support." />
        <meta name="keywords" content="login, sign in, Sahla Curtain Center, customer account, secure login, curtains, Kerala, register, authentication" />
        <link rel="canonical" href="https://sccstore.com/login" />
        {/* Open Graph */}
        <meta property="og:title" content="Login | Sahla Curtain Center - Secure Customer Sign In" />
        <meta property="og:description" content="Sign in to your Sahla Curtain Center account for a personalized shopping experience. Secure login for premium curtains, orders, and support." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sccstore.com/login" />
        <meta property="og:image" content="https://sccstore.com/preview.jpg" />
        <meta property="og:site_name" content="Sahla Curtain Center" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Login | Sahla Curtain Center - Secure Customer Sign In" />
        <meta name="twitter:description" content="Sign in to your Sahla Curtain Center account for a personalized shopping experience. Secure login for premium curtains, orders, and support." />
        <meta name="twitter:image" content="https://sccstore.com/preview.jpg" />
        <meta name="twitter:site" content="@sccstore" />
        {/* Social Profiles */}
        <meta property="og:see_also" content="https://www.instagram.com/sahlacurtaincenter/" />
        <meta property="og:see_also" content="https://www.facebook.com/people/Sahla-Curtain-Center/61565451845405/" />
        <meta property="og:see_also" content="https://www.youtube.com/@sahlacurtaincenter" />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Login | Sahla Curtain Center",
            "description": "Sign in to your Sahla Curtain Center account for a personalized shopping experience. Secure login for premium curtains, orders, and support.",
            "url": "https://sccstore.com/login",
            "image": "https://sccstore.com/preview.jpg",
            "publisher": {
              "@type": "Organization",
              "name": "Sahla Curtain Center",
              "logo": {
                "@type": "ImageObject",
                "url": "https://sccstore.com/favicon.png"
              },
              "sameAs": [
                "https://www.instagram.com/sahlacurtaincenter/",
                "https://www.facebook.com/people/Sahla-Curtain-Center/61565451845405/",
                "https://www.youtube.com/@sahlacurtaincenter"
              ]
            }
          }
        `}</script>
      </Helmet>

      {authStatus && (
        <AuthStatusModal 
          status={authStatus} 
          onClose={() => setAuthStatus(null)} 
        />
      )}

      <form onSubmit={handleSubmit} className="flex flex-col items-center w-[90%] sm:max-w-96 m-auto mt-14 gap-4">
        <div className="inline-flex items-center gap-2 mb-2 mt-10">
          <p className="prata-regular text-3xl text-gray-800">Sign In</p>
          <hr className="border-none h-[1.5px] w-8 bg-gray-800" />
        </div>

        <p className="text-gray-600 text-sm mb-4 text-center">
          Sign in to your account and enjoy personalized features.
        </p>

        {/* Email input */}
        <div className="relative w-full">
          <FiMail className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
          <input
            onChange={handleChange}
            type="email"
            name="email"
            value={formData.email}
            className="w-full pl-10 pr-3 py-2 border border-gray-800 bg-black text-white placeholder-gray-500"
            placeholder="Email"
            required
          />
        </div>

        {/* Password input */}
        <div className="relative w-full">
          <FiLock className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
          <input
            onChange={handleChange}
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            className="w-full pl-10 pr-10 py-2 border border-gray-800 bg-black text-white placeholder-gray-500"
            placeholder="Password"
            required
            minLength={6}
          />
          <div
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-200"
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </div>
        </div>

        {/* Term and conditions */}
        <div className="flex items-start w-full text-sm mt-2">
          <input
            type="checkbox"
            id="terms"
            className="mt-1 mr-2 rounded border-gray-300 text-gray-800 focus:ring-gray-800"
            checked={agreeTerms}
            onChange={() => setAgreeTerms(!agreeTerms)}
          />
          <label htmlFor="terms" className="text-gray-600 leading-tight">
            I agree to the&nbsp;
            <Link to="/terms-and-conditions" className="text-gray-800 underline hover:text-gray-600 font-medium">Term and Conditions</Link>
            &nbsp;and&nbsp;
            <Link to="/privacy-policies" className="text-gray-800 underline hover:text-gray-600 font-medium">Privacy Policy</Link>
          </label>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!agreeTerms}
          className={`w-full font-medium px-8 py-2.5 mt-4 rounded transition flex items-center justify-center gap-2 ${
            agreeTerms
              ? 'bg-gray-800 text-white hover:bg-gray-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <FiLogIn /> Sign In
        </button>

        {/* Google Login */}
        <div className="w-full mt-4">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => console.error('Google Login Failed')}
            useOneTap
            theme="filled_black"
            size="large"
            text="continue_with"
            shape="rectangular"
            width="100%"
            ux_mode="popup"
          />
        </div>

        <div className="text-center mt-4 text-gray-600">
          Don't have an account?&nbsp;
          <Link to="/register" className="text-gray-800 underline hover:text-gray-600 font-medium">
            Sign Up
          </Link>
        </div>
      </form>
    </>
  );
};

export default Login;
